import React, { useState, useCallback } from "react";
import Swal from "sweetalert2";
import moment from "moment";
import useAuthFetch from "../../hooks/useAuthFetch";

const SWAL_THEME = {
  background: "#76236C",
  color: "#fafafa",
  confirmButtonColor: "#19D3C5",
};

const DEFAULT_PAGE_SIZE = 10;

function isPagedPreview(body) {
  return body && typeof body === "object" && Array.isArray(body.data) && typeof body.total === "number";
}

/**
 * @param {object} props
 * @param {string} props.reportBase - e.g. "reportes/ventas" or "reportes/ventas-promocionales" (no leading slash)
 * @param {string} props.description
 * @param {string} props.emptyMessage - when preview returns []
 * @param {string} props.downloadFilenamePrefix - e.g. "reporte-ventas"
 * @param {{ singular: string, plural: string }} props.previewCountSuffix
 */
export function VentasReportePanel({
  reportBase,
  description,
  emptyMessage,
  downloadFilenamePrefix,
  previewCountSuffix,
}) {
  const authFetch = useAuthFetch();
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);

  const validateDates = () => {
    if (!fechaInicio || !fechaFin) {
      setError("Ambas fechas son obligatorias.");
      return false;
    }
    if (fechaInicio > fechaFin) {
      setError("La fecha de inicio no puede ser posterior a la fecha de fin.");
      return false;
    }
    setError("");
    return true;
  };

  const fetchPreviewPage = useCallback(
    async (page, limit = DEFAULT_PAGE_SIZE) => {
      if (!validateDates()) return;
      setLoadingPreview(true);
      try {
        const res = await authFetch(
          `${reportBase}/preview?fechaInicio=${fechaInicio}&fechaFin=${fechaFin}&page=${page}&limit=${limit}`
        );
        if (!res || res.status !== 200) {
          Swal.fire({ icon: "error", title: "Error", text: "No se pudo cargar la vista previa.", ...SWAL_THEME });
          return;
        }
        const body = await res.json();
        if (!isPagedPreview(body)) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Respuesta de vista previa no reconocida. Actualizá el servidor.",
            ...SWAL_THEME,
          });
          return;
        }
        setPreview(body);
      } finally {
        setLoadingPreview(false);
      }
    },
    [authFetch, reportBase, fechaInicio, fechaFin]
  );

  const handlePreview = () => {
    fetchPreviewPage(1, DEFAULT_PAGE_SIZE);
  };

  const handlePrevPage = () => {
    if (!preview || preview.page <= 1) return;
    fetchPreviewPage(preview.page - 1, preview.limit);
  };

  const handleNextPage = () => {
    if (!preview) return;
    const { page, limit, total } = preview;
    if (page * limit >= total) return;
    fetchPreviewPage(page + 1, limit);
  };

  const handleDescargar = async () => {
    if (!validateDates()) return;
    setLoading(true);
    try {
      const res = await authFetch(`${reportBase}?fechaInicio=${fechaInicio}&fechaFin=${fechaFin}`);
      if (!res) {
        Swal.fire({ icon: "error", title: "Error de red", text: "No se pudo conectar con el servidor.", ...SWAL_THEME });
        return;
      }
      if (res.status === 200) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${downloadFilenamePrefix}-${fechaInicio}-${fechaFin}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        Swal.fire({ icon: "error", title: "Error", text: "No fue posible generar el reporte. Intentá de nuevo.", ...SWAL_THEME });
      }
    } finally {
      setLoading(false);
    }
  };

  const rangeLabel = () => {
    if (!preview || preview.total === 0) return null;
    const { data, page, limit, total } = preview;
    const from = (page - 1) * limit + 1;
    const to = (page - 1) * limit + data.length;
    return (
      <span>
        Mostrando <strong>{from}</strong>–<strong>{to}</strong> de <strong>{total}</strong>{" "}
        {total !== 1 ? previewCountSuffix.plural : previewCountSuffix.singular}
      </span>
    );
  };

  return (
    <div>
      <p className="text-sm text-gray-500 mb-6">{description}</p>
      <div className="max-w-xl">
        <div>
          <label className="text-base">Fecha de inicio</label>
          <input
            type="date"
            className="w-full rounded-3xl my-2 h-12 p-6 border border-purple"
            value={fechaInicio}
            onChange={(e) => {
              setFechaInicio(e.target.value);
              setPreview(null);
            }}
          />
        </div>
        <div>
          <label className="text-base">Fecha de fin</label>
          <input
            type="date"
            className="w-full rounded-3xl my-2 h-12 p-6 border border-purple"
            value={fechaFin}
            onChange={(e) => {
              setFechaFin(e.target.value);
              setPreview(null);
            }}
          />
        </div>
        {error && <p className="text-pink text-center mt-4">{error}</p>}
        <div className="flex gap-3 mt-6">
          <button
            type="button"
            onClick={handlePreview}
            disabled={loadingPreview || loading}
            className="border border-purple text-purple h-12 px-8 rounded-full flex-1 disabled:opacity-50"
          >
            {loadingPreview ? "Cargando..." : "Ver datos"}
          </button>
          <button
            type="button"
            onClick={handleDescargar}
            disabled={loading || loadingPreview}
            className="bg-purple text-white h-12 px-8 rounded-full flex-1 disabled:opacity-50"
          >
            {loading ? "Generando..." : "Descargar reporte"}
          </button>
        </div>
      </div>

      {preview !== null && (
        <div className="mt-8">
          {preview.total === 0 ? (
            <p className="text-gray-500 text-sm">{emptyMessage}</p>
          ) : (
            <>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3 text-sm text-gray-500">
                <p>{rangeLabel()}</p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handlePrevPage}
                    disabled={loadingPreview || preview.page <= 1}
                    className="px-4 py-2 rounded-full border border-purple text-purple text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-purple hover:bg-opacity-10"
                  >
                    Anterior
                  </button>
                  <span className="text-gray-600 tabular-nums px-1">
                    Página {preview.page} / {Math.max(1, Math.ceil(preview.total / preview.limit))}
                  </span>
                  <button
                    type="button"
                    onClick={handleNextPage}
                    disabled={loadingPreview || preview.page * preview.limit >= preview.total}
                    className="px-4 py-2 rounded-full border border-purple text-purple text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-purple hover:bg-opacity-10"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full text-sm text-left">
                  <thead className="bg-purple text-white">
                    <tr>
                      <th className="px-4 py-3 font-semibold"># Orden</th>
                      <th className="px-4 py-3 font-semibold">Fecha de Venta</th>
                      <th className="px-4 py-3 font-semibold">Producto</th>
                      <th className="px-4 py-3 font-semibold text-right">Peso (kg)</th>
                      <th className="px-4 py-3 font-semibold text-right">Cantidad</th>
                      <th className="px-4 py-3 font-semibold text-right">Lista sin IVA</th>
                      <th className="px-4 py-3 font-semibold text-right">Lista con IVA</th>
                      <th className="px-4 py-3 font-semibold text-right">% Desc.</th>
                      <th className="px-4 py-3 font-semibold text-right">Monto Desc.</th>
                      <th className="px-4 py-3 font-semibold text-right">Precio c/ Desc.</th>
                      <th className="px-4 py-3 font-semibold">Método pago</th>
                      <th className="px-4 py-3 font-semibold">Provincia</th>
                      <th className="px-4 py-3 font-semibold">Distrito</th>
                      <th className="px-4 py-3 font-semibold">Tipo Documento</th>
                      <th className="px-4 py-3 font-semibold">Facturación</th>
                      <th className="px-4 py-3 font-semibold">Ticolitas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.data.map((row, i) => (
                      <tr key={`${row.numeroOrden}-${row.fechaVenta}-${i}`} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                        <td className="px-4 py-2 font-medium">{row.numeroOrden}</td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          {row.fechaVenta ? moment(row.fechaVenta).format("DD/MM/YYYY") : "-"}
                        </td>
                        <td className="px-4 py-2 max-w-xs">{row.producto}</td>
                        <td className="px-4 py-2 text-right whitespace-nowrap tabular-nums">
                          {row.pesoKg != null && row.pesoKg !== ""
                            ? `${Number(row.pesoKg).toLocaleString("es-CR", { minimumFractionDigits: 3, maximumFractionDigits: 3 })}`
                            : "-"}
                        </td>
                        <td className="px-4 py-2 text-right">{row.cantidad ?? "-"}</td>
                        <td className="px-4 py-2 text-right whitespace-nowrap">
                          ₡{Number(row.precioListaSinIva).toLocaleString("es-CR", { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-4 py-2 text-right whitespace-nowrap">
                          ₡{Number(row.precioListaConIva).toLocaleString("es-CR", { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-4 py-2 text-right">{Number(row.porcentajeDescuento).toFixed(2)}%</td>
                        <td className="px-4 py-2 text-right whitespace-nowrap">
                          ₡{Number(row.montoDescuento).toLocaleString("es-CR", { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-4 py-2 text-right whitespace-nowrap">
                          ₡{Number(row.precioVentaConDescuento).toLocaleString("es-CR", { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-4 py-2">{row.metodoPago || "-"}</td>
                        <td className="px-4 py-2">{row.provincia || "-"}</td>
                        <td className="px-4 py-2">{row.distrito || "-"}</td>
                        <td className="px-4 py-2">{row.tipoDocumento || "-"}</td>
                        <td className="px-4 py-2">
                          {row.enlaceLucida ? (
                            <a href={row.enlaceLucida} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                              Ver factura
                            </a>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="px-4 py-2">
                          <a href={row.enlaceTicolitas} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                            Ver pedido
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default VentasReportePanel;
